/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import React, { useState, useRef, useLayoutEffect } from 'react';
import {
  Box, Divider, Stack, Typography, Link, IconButton,
} from '@mui/material';
import { Link as routerLink, useParams } from 'react-router-dom';
import {
  CopyButton,
  formatPhoneNumber,
  formatTimeWithTimeZoneName,
  getBudgetPerPersonString,
  getFormattedDateShortMonthDDYYYY, getTotalBudgetString, OtherChips, OutlinedIcons, TripMatchChips, useApiClient, useCurrentTime, useFetch, useFetchImmutable,
} from '@marageti/z4-lib';
import {
  Conversation, TripMatch, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { ReferralType } from '@marageti/z4-sdk/lib/travel/TripRequest';
import { useConversationsStore } from '../../../../store';
import { formatCountryCodesToString } from '../../../../components/trips-card/utils';
import useFetchItineraryPath from '../../../../hooks/use-fetch-itinerary-path';

const { OutlinedExternalLinkIcon } = OutlinedIcons;

const { ChipRepeat, ChipPRL } = OtherChips;
const { getTripMatchStatusChip } = TripMatchChips;

export type SideBarItemsProps = {
  items: ({ title: string | React.ReactNode, content?: React.ReactNode } | React.ReactNode)[],
  chips?: React.ReactNode,
};

const truncateStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 10,
  overflow: 'hidden',
};

const TripDescription = ({ description }: any) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const textRef = useRef<HTMLDivElement>(null);
  const hasContent = !!description?.length;

  const toggleExpanded = () => setExpanded((ex) => !ex);

  useLayoutEffect(() => {
    if (!hasContent || expanded) {
      setIsOverflowing(false);
      return;
    }
    const el = textRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight);
  }, [description, hasContent, expanded]);

  const textStyle = hasContent && !expanded ? truncateStyle : {};

  return (
    <Stack>
      <Typography ref={textRef} component="div" color="text.secondary" style={textStyle}>
        {!hasContent ? '-' : description}
      </Typography>
      {(isOverflowing || expanded) && (
      <Typography
        component="span"
        variant="captionBold"
        color="primary"
        style={{ cursor: 'pointer' }}
        onClick={toggleExpanded}
      >
        {expanded ? 'Show Less' : 'Show More'}
      </Typography>
      )}
    </Stack>
  );
};

const SideBarItems = ({ items, chips = undefined }: SideBarItemsProps) => (
  <Stack spacing={2}>
    {chips && (
      <Stack direction="row" spacing={1}>
        {chips}
      </Stack>
    )}
    {items.map((item, index) => {
      if (typeof item === 'object' && item !== null && 'title' in item) {
        return (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography component="div" variant="caption" fontWeight="bold" color="textPrimary">{item.title}</Typography>
            <Typography component="div" color="text.secondary" display="flex" alignItems="center">{item.content || '-'}</Typography>
          </Box>
        );
      }
      return (
        <React.Fragment key={index}>
          {item}
        </React.Fragment>
      );
    })}
  </Stack>
);

const SIDEBAR_OPEN_WIDTH = 320;

const getPossessive = (name: string | undefined): string => {
  if (!name?.trim()) return '';
  const trimmed = name.trim();
  if (trimmed.endsWith('s')) return `${trimmed}'`;
  return `${trimmed}'s`;
};

export type RightSidebarProps = {
  isRightSidebarOpen: boolean;
  conversation?: Conversation;
};

const RightSidebar = ({ isRightSidebarOpen, conversation: conversationProp }: RightSidebarProps) => {
  const apiClient = useApiClient();
  const { conversationsMap } = useConversationsStore();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const conversationFromStore = conversationsMap?.[conversationId!];
  const conversation = conversationProp ?? conversationFromStore;
  const shouldFetchSidebarData = isRightSidebarOpen && !!conversation;
  const { data: tripRequest } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [conversation?.tripRequestId],
    shouldFetchSidebarData && !!conversation?.tripRequestId,
  );
  const { data: tripMatch } = useFetch<TripMatch>(
    apiClient.tripMatchesClient.getTripMatch,
    [conversation?.tripMatchId],
    shouldFetchSidebarData && !!conversation?.tripMatchId,
  );
  const countryCodes = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], shouldFetchSidebarData);
  const [time] = useCurrentTime(tripMatch?.travelers[0]?.timeZone?.name);
  const itineraryPath = useFetchItineraryPath(tripRequest?.basedOnItineraryId);

  const basedOnItineraryContent = itineraryPath ? (
    <Link href={itineraryPath} target="_blank" rel="noopener noreferrer">Link to Itinerary</Link>
  ) : '-';

  return (
    <Box
      sx={{
        ml: isRightSidebarOpen ? 3 : 0,
        flex: `0 0 ${isRightSidebarOpen ? SIDEBAR_OPEN_WIDTH : 0}px`,
        width: isRightSidebarOpen ? `${SIDEBAR_OPEN_WIDTH}px` : 0,
        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), flex 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: `${SIDEBAR_OPEN_WIDTH}px`,
          height: '100%',
          padding: '16px 20px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          pb: 4,
          transform: isRightSidebarOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: 'background.default',
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {(tripRequest && tripMatch && countryCodes.data) && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h3" sx={{ mr: 1 }}>
                {getPossessive(tripMatch?.travelers[0]?.fullName)}
                {' '}
                Trip
              </Typography>
              <IconButton
                aria-label="To trip match"
                component={routerLink}
                to={`/trip-matches/${tripMatch.id}`}
                target="_blank"
                color="inherit"
              >
                <OutlinedExternalLinkIcon fontSize="small" />
              </IconButton>
            </Box>
            <SideBarItems
              items={[
                {
                  title: 'Phone',
                  content: tripMatch.travelers[0]?.phone.number ? (
                    <>
                      {formatPhoneNumber(tripMatch.travelers[0].phone.prefix, tripMatch.travelers[0].phone.number)}
                      <CopyButton textToCopy={`${tripMatch.travelers[0].phone.prefix} ${tripMatch.travelers[0].phone.number}`} iconFontSize="small" size="small" />
                    </>
                  ) : (
                    '-'
                  ),
                },
                { title: 'Timezone', content: formatTimeWithTimeZoneName(tripMatch?.travelers[0]?.timeZone, time) },
              ]}
              chips={[
                tripMatch.status ? getTripMatchStatusChip(tripMatch.status, 'small') : null,
                tripMatch.repeatTraveler && <ChipRepeat key="repeat" />,
                tripMatch.referralType === (ReferralType.OneTimePartnerReferral || ReferralType.LifetimePartnerReferral) && <ChipPRL key="prl" />,
              ].filter(Boolean)}
            />
            <Divider sx={{ my: 2 }} />
            <SideBarItems
              items={[
                { title: 'Description', content: (<TripDescription description={tripRequest.tripDescription} />) },
                { title: 'Countries', content: countryCodes && formatCountryCodesToString(tripMatch.countries, countryCodes.data) },
                { title: 'When', content: `${getFormattedDateShortMonthDDYYYY(tripMatch.departureDate) || ''} (${tripMatch.totalNights} Nights)` },
                { title: 'Budget Per Person', content: `${getBudgetPerPersonString(tripMatch.budget, tripMatch.numberOfAdults, tripMatch.numberOfChildren)}` },
                { title: 'Total Budget', content: `${getTotalBudgetString(tripMatch.budget)}` },
                { title: 'Travelers', content: `${tripMatch.numberOfAdults} Adults, ${tripMatch.numberOfChildren} Children` },
                { title: 'Based on Itinerary', content: basedOnItineraryContent },
                { title: 'Last Updated', content: getFormattedDateShortMonthDDYYYY(tripMatch.updatedAt) || '' },
                {
                  title: 'Reference ID',
                  content: tripRequest?.referenceId && (
                  <>
                    {tripRequest.referenceId}
                    <CopyButton textToCopy={tripRequest.referenceId} iconFontSize="small" size="small" />
                  </>
                  ),
                },
              ]}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default RightSidebar;
